import React, { useMemo } from "react";

const COLORS = {
  present: "#10b981",
  absent: "#ef4444",
  leave: "#f59e0b",
  halfDay: "#7c3aed",
  late: "#e11d48",
  empty: "#e2e8f0",
  track: "#f1f5f9",
};

//Created By Cursor
function toCount(value) {
  if (value === null || value === undefined || value === "" || value === "N/A") {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

//Created By Cursor
function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

//Created By Cursor
function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    "M",
    start.x,
    start.y,
    "A",
    r,
    r,
    0,
    largeArc,
    0,
    end.x,
    end.y,
  ].join(" ");
}

//Created By Cursor
/**
 * Modern donut chart for attendance P / A / L.
 * Props: Present, Absent, Leave (number | "N/A")
 * Optional: size (px), showLegend (bool), compact (bool)
 */
function AttendancePieChart({
  Present,
  Absent,
  Leave,
  HalfDay,
  Late,
  size = 112,
  showLegend = true,
  compact = false,
}) {
  const present = toCount(Present);
  const absent = toCount(Absent);
  const leave = toCount(Leave);
  const halfDay = toCount(HalfDay);
  const late = toCount(Late);
  const showHalfDay = HalfDay !== undefined;
  const showLate = Late !== undefined;
  const hasData =
    present !== null ||
    absent !== null ||
    leave !== null ||
    halfDay !== null ||
    late !== null;

  const p = present ?? 0;
  const a = absent ?? 0;
  const l = leave ?? 0;
  const hd = halfDay ?? 0;
  const la = late ?? 0;
  const total = p + a + l + hd + la;

  const segments = useMemo(() => {
    if (!hasData || total <= 0) return [];
    const raw = [
      { key: "P", label: "Present", value: p, color: COLORS.present },
      { key: "A", label: "Absent", value: a, color: COLORS.absent },
      { key: "L", label: "Leave", value: l, color: COLORS.leave },
      { key: "HD", label: "Half Day", value: hd, color: COLORS.halfDay },
      { key: "LA", label: "Late", value: la, color: COLORS.late },
    ].filter((s) => s.value > 0);

    let angle = 0;
    return raw.map((s) => {
      const sweep = (s.value / total) * 360;
      const start = angle;
      const end = angle + sweep;
      angle = end;
      return { ...s, start, end: Math.min(end, 359.999) };
    });
  }, [hasData, total, p, a, l, hd, la]);

  const cx = size / 2;
  const cy = size / 2;
  const stroke = Math.max(10, Math.round(size * 0.14));
  const radius = size / 2 - stroke / 2 - 2;

  const centerSize = Math.max(16, Math.round(size * 0.12));
  const centerLabel = !hasData ? "—" : String(total);
  const centerSub = !hasData ? "N/A" : "Total";

  return (
    <div
      className={`att-donut${compact ? " att-donut--compact" : ""}${
        showLegend ? "" : " att-donut--chart-only"
      }`}
    >
      <div className="att-donut__chart" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label={
            hasData
              ? `Attendance total ${total}: Present ${p}, Absent ${a}, Leave ${l}${
                  showHalfDay ? `, Half Day ${hd}` : ""
                }${showLate ? `, Late ${la}` : ""}`
              : "Attendance not available"
          }
        >
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke={COLORS.track}
            strokeWidth={stroke}
          />
          {!hasData || total <= 0 ? (
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={COLORS.empty}
              strokeWidth={stroke}
              strokeDasharray="4 6"
            />
          ) : segments.length === 1 ? (
            <circle
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={segments[0].color}
              strokeWidth={stroke}
              strokeLinecap="round"
            />
          ) : (
            segments.map((s) => (
              <path
                key={s.key}
                d={describeArc(cx, cy, radius, s.start, s.end)}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeLinecap="butt"
              />
            ))
          )}
        </svg>
        <div className="att-donut__center">
          <strong style={{ fontSize: centerSize }}>{centerLabel}</strong>
          <span>{centerSub}</span>
        </div>
      </div>

      {showLegend ? (
        <ul className="att-donut__legend" aria-label="Attendance breakdown">
          <li>
            <span className="att-donut__swatch att-donut__swatch--p" />
            <em>Present</em>
            <strong>{hasData ? p : "—"}</strong>
          </li>
          <li>
            <span className="att-donut__swatch att-donut__swatch--a" />
            <em>Absent</em>
            <strong>{hasData ? a : "—"}</strong>
          </li>
          <li>
            <span className="att-donut__swatch att-donut__swatch--l" />
            <em>Leave</em>
            <strong>{hasData ? l : "—"}</strong>
          </li>
          {showHalfDay ? (
            <li>
              <span className="att-donut__swatch att-donut__swatch--hd" />
              <em>Half Day</em>
              <strong>{hasData ? hd : "—"}</strong>
            </li>
          ) : null}
          {showLate ? (
            <li>
              <span className="att-donut__swatch att-donut__swatch--la" />
              <em>Late</em>
              <strong>{hasData ? la : "—"}</strong>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

export default AttendancePieChart;
